import { motion, AnimatePresence } from "framer-motion";
import { X, Headphones, Phone, MessageCircle } from "lucide-react";

// Support phones — clicking the WhatsApp icon opens wa.me with a pre-filled
// "السلام عليكم" greeting. Clicking the Phone icon dials directly.
const SUPPORT_PHONES = [
  { local: "07717637525", intl: "9647717637525", label: "خدمة العملاء" },
  { local: "07842220088", intl: "9647842220088", label: "الدعم الفني" },
];

const GREETING = "السلام عليكم، أحتاج مساعدة من خدمة جملتي";

const CustomerServiceModal = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="cs-modal-overlay"
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cs-modal"
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
          >
            {/* Decorative header */}
            <div className="relative bg-gradient-to-br from-[#6B8E45] via-[#4A6F36] to-[#2D4A3E] text-white p-6 text-center">
              <button
                onClick={onClose}
                data-testid="cs-modal-close"
                className="absolute top-3 left-3 p-2 bg-white/20 backdrop-blur rounded-full hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <motion.div
                animate={{ rotate: [0, -8, 8, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-20 h-20 mx-auto mb-3 bg-white/15 backdrop-blur rounded-full flex items-center justify-center border-2 border-white/30"
              >
                <Headphones className="w-10 h-10" />
              </motion.div>
              <h2 className="font-heading font-bold text-2xl">خدمة العملاء</h2>
              <p className="text-sm opacity-90 mt-1">نحن دائماً معك</p>
            </div>

            {/* Body — one row per support phone, each with its own call + WhatsApp button */}
            <div className="p-6 space-y-3">
              <p className="text-xs text-center text-[#5C6E64] mb-2">
                اضغط على أيقونة الواتساب لإرسال رسالة جاهزة، أو على الهاتف للاتصال مباشرة.
              </p>

              {SUPPORT_PHONES.map((p) => (
                <div
                  key={p.local}
                  data-testid={`cs-row-${p.local}`}
                  className="flex items-center justify-between gap-2 bg-[#F3F6F4] rounded-2xl p-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-[#5C6E64] mb-0.5">{p.label}</p>
                    <p
                      data-testid={`cs-phone-${p.local}`}
                      dir="ltr"
                      className="font-bold text-lg text-[#2D4A3E] tracking-wide"
                    >
                      {p.local}
                    </p>
                  </div>
                  <a
                    href={`tel:${p.local}`}
                    data-testid={`cs-call-${p.local}`}
                    title="اتصال"
                    className="w-10 h-10 flex items-center justify-center bg-[#2D4A3E] hover:bg-[#1f3329] text-white rounded-full transition-colors shadow"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                  <a
                    href={`https://wa.me/${p.intl}?text=${encodeURIComponent(GREETING)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`cs-whatsapp-${p.local}`}
                    title="مراسلة على واتساب"
                    className="w-10 h-10 flex items-center justify-center bg-[#25D366] hover:bg-[#1ebd5b] text-white rounded-full transition-colors shadow"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </a>
                </div>
              ))}

              <p className="text-xs text-center text-[#5C6E64] pt-2">
                أوقات الدوام: السبت - الخميس · 9 صباحاً - 9 مساءً
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CustomerServiceModal;
